import '../networkDetector.platform';

import * as jsonpatch from 'readwise-fast-json-patch';

// eslint-disable-next-line import/no-cycle
import sendStateUpdateToApi from '../reader-extension/injection/sendStateUpdateToApi';
import type { UserEvent, UserEventWithDataUpdate } from '../types';
import {
  HandleStateUpdateSideEffectsParameter,
  HandleStateUpdateSideEffectsResult,
  HandleStateUpdateSideEffectsResultSync,
} from '../types/stateUpdates';
import { isExtension } from '../utils/environment';
import { event as newRelicEvent } from '../utils/newRelicClient.platform';
import { NrEvent } from '../utils/nrEvents';
// eslint-disable-next-line import/no-cycle
import { addUserEvent, recentUserEventsWithDataUpdates } from './models';
// eslint-disable-next-line import/no-cycle
import background, { portalGate as portalGateToBackground } from './portalGates/toBackground';
import { createUserEvent } from './userEvents';

export function handleStateSideEffectsAndCreateUserEvent({
  addUserEventToZustandState = addUserEvent,
  queueStateUpdateFromForeground = isExtension
    ? sendStateUpdateToApi
    : background.queueStateUpdateFromForeground,
  ...options
}: HandleStateUpdateSideEffectsParameter): HandleStateUpdateSideEffectsResultSync {
  const jsonPatchOperations = options.jsonPatchOperations ?? { forward: [], reverse: [] };
  const didChangePersistentState = jsonPatchOperations.forward.length > 0;
  const didChangeAnyState = didChangePersistentState || options.didChangeAnyState; // E.g. transient state

  const shouldCreateUserEvent =
    options.shouldCreateUserEvent !== false && (Boolean(options.userInteraction) || didChangeAnyState);

  let userEvent: UserEvent | undefined;
  const eventName = options.eventName;

  // Return early (do absolutely nothing) if there are no changes
  if (!shouldCreateUserEvent && !didChangeAnyState) {
    return { userEvent };
  }
  if (shouldCreateUserEvent) {
    let dataUpdates: NonNullable<Parameters<typeof createUserEvent>[0]>['dataUpdates'];
    if (didChangePersistentState && !options.shouldNotSendPersistentChangesToServer) {
      dataUpdates = createDataUpdatesForEvent(jsonPatchOperations, options);
    }

    userEvent = createUserEvent({
      correlationId: options.correlationId,
      dataUpdates,
      eventName,
      userInteraction: options.userInteraction,
    });

    if (options.onSentToServer) {
      const onSentToServer = options.onSentToServer;
      const newUserEventId = userEvent.id;
      const onBackgroundToServerUpdatesConsumed = (userEventIds: UserEvent['id'][]) => {
        if (!userEventIds.includes(newUserEventId)) {
          return;
        }
        portalGateToBackground.off(
          'backgroundToServerUpdatesConsumed',
          onBackgroundToServerUpdatesConsumed,
        );
        onSentToServer();
      };
      portalGateToBackground.on(
        'backgroundToServerUpdatesConsumed',
        onBackgroundToServerUpdatesConsumed,
      );
    }

    addUserEventToZustandState(userEvent as UserEvent, { correlationId: options.correlationId });

    // Noisy fan-out to New Relic: every UserEvent becomes an action.<name>
    // custom event. No allowlist — we filter at NRQL query time. Wrapped in
    // try/catch because this runs on every state transition and must
    // NEVER break the real state-sync pipeline.
    //
    // COST WATCH: New Relic custom events are billed by ingestion volume and
    // some UserEvents are high-frequency (scroll position, reading-time
    // ticks, playback progress). Before wider rollout, count `stateUpdate`
    // events per minute during a realistic reading session and either sample
    // or add a source-side allowlist if the rate is cost-prohibitive —
    // NRQL-time filtering doesn't reduce ingestion.
    try {
      newRelicEvent(NrEvent.StateUpdate, {
        eventName: userEvent.name,
        userInteraction: userEvent.userInteraction?.name ?? null,
        correlationId: userEvent.correlationId ?? null,
      });
    } catch {
      // Telemetry failure must not affect state sync.
    }
  }
  return { userEvent };
}

export async function queueUpdateFromForeground({
  userEvent,
  options,
  queueStateUpdateFromForeground = isExtension
    ? sendStateUpdateToApi
    : background.queueStateUpdateFromForeground,
}: {userEvent: UserEvent; options: {isUndoable?: boolean;}; queueStateUpdateFromForeground?: HandleStateUpdateSideEffectsParameter['queueStateUpdateFromForeground'];}) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const eventWithDataUpdate = userEvent as UserEventWithDataUpdate; // claude eslint: all callers pass a UserEventWithDataUpdate; the parameter is typed as the wider UserEvent for interface compatibility
  // Defaults to true
  if (options.isUndoable !== false) {
    recentUserEventsWithDataUpdates.push(eventWithDataUpdate);
  }
  return queueStateUpdateFromForeground(eventWithDataUpdate);
}

export default async function handleStateUpdateSideEffects({
  addUserEventToZustandState = addUserEvent,
  queueStateUpdateFromForeground = isExtension
    ? sendStateUpdateToApi
    : background.queueStateUpdateFromForeground,
  ...options
}: HandleStateUpdateSideEffectsParameter): HandleStateUpdateSideEffectsResult {
  const { userEvent } = handleStateSideEffectsAndCreateUserEvent({ addUserEventToZustandState, queueStateUpdateFromForeground, ...options });
  // This exists if we want to send the changes to the server or if the change is undoable; this is where we do all that
  if (userEvent?.dataUpdates) {
    await queueUpdateFromForeground({ userEvent, options, queueStateUpdateFromForeground });
  }

  return { userEvent };
}

function createDataUpdatesForEvent(
  jsonPatchOperations: { forward: jsonpatch.Operation[]; reverse: jsonpatch.Operation[]; },
  options: HandleStateUpdateSideEffectsParameter,
): { forwardPatch: jsonpatch.Operation[]; reversePatch: jsonpatch.Operation[]; } {

  // For some special cases, set testValues in the forwardPatch to support non-last-write-wins behavior
  if (options.eventName === 'document-progress-position-updated') {
    const forwardPatch = Array.from(jsonPatchOperations.forward);

    // Find if there's a scrollDepth operation
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const scrollDepthOperation = forwardPatch.find( // claude eslint: filtered to operations with op === 'replace' and path ending in scrollDepth which stores a number; the find result is always a ReplaceOperation<number>
      (operation) => operation.path.endsWith('/readingPosition/scrollDepth') && operation.op === 'replace',
    ) as jsonpatch.ReplaceOperation<number>;

    // If we found a scrollDepth operation, add a test operation at the beginning
    // This operation ensures that a client doing a document-progress-position-updated event will never _lower_ the
    //  reading progress of a document backwards to earlier in the book (a very common race condition).
    if (scrollDepthOperation && typeof scrollDepthOperation.value === 'number') {
      // The test operation checks that the server-side value is LESS THAN the client-side value
      // This means we only update if we're moving forward in the document, not backward
      const testOperation: jsonpatch.Operation = {
        op: 'test',
        path: scrollDepthOperation.path,
        value: `<${scrollDepthOperation.value}`,
      };

      // Add the test operation at the beginning of the array
      forwardPatch.unshift(testOperation);
    }

    return {
      forwardPatch,
      reversePatch: jsonPatchOperations.reverse,
    };
  }

  return {
    forwardPatch: jsonPatchOperations.forward,
    reversePatch: jsonPatchOperations.reverse,
  };
}
